/**
 * Gain Calculations
 *
 * Functions for calculating audio gain based on distance
 * Uses CUBIC EXPONENTIAL equation for clearly NOTICEABLE volume changes
 */

/**
 * Gain calculation configuration
 */
export interface GainConfig {
  minDistance?: number; // Full volume at this distance (meters)
  maxDistance?: number; // Silent beyond this distance (meters)
}

/**
 * Default gain configuration
 * CUBIC FALLOFF settings for noticeable volume changes:
 * - 0.5m or closer: 100% volume
 * - 5m: ~33% volume (clearly quieter)
 * - 10m: ~4% volume (barely audible)
 * - 15m or further: 0% volume (SILENT - complete cutoff)
 */
export const DEFAULT_GAIN_CONFIG: Required<GainConfig> = {
  minDistance: 0.5, // Full volume at 0.5m or closer
  maxDistance: 15.0, // Silent at 15m
};

/**
 * Calculate gain based on distance using CUBIC EXPONENTIAL equation
 *
 * Uses CUBIC (cubed) falloff for clearly NOTICEABLE volume drops:
 * Gain = 100 * (1 - normalized)³
 *
 * Distance → Gain mapping (with 0.5m-15m range):
 * ┌──────────────┬─────────┬────────────────────────────────┐
 * │ Distance     │ Gain %  │ Description                    │
 * ├──────────────┼─────────┼────────────────────────────────┤
 * │ 0.0 - 0.5m   │ 100%    │ Full volume (intimate)         │
 * │ 1.0m         │ ~90%    │ Very close - still loud        │
 * │ 2.0m         │ ~72%    │ Normal talking - NOTICEABLE    │
 * │ 3.0m         │ ~57%    │ Across table - CLEARLY QUIETER │
 * │ 5.0m         │ ~33%    │ Across room - MUCH QUIETER     │
 * │ 7.0m         │ ~17%    │ Far end of room - very faint   │
 * │ 10.0m        │ ~4%     │ Barely audible                 │
 * │ ≥15.0m       │ 0%      │ Silent (cutoff)                │
 * └──────────────┴─────────┴────────────────────────────────┘
 *
 * @param distance Distance in meters
 * @param config Optional gain configuration
 * @returns Gain as percentage (0-100)
 */
export function calculateLogarithmicGain(
  distance: number,
  config: GainConfig = {}
): number {
  const {minDistance, maxDistance} = {
    ...DEFAULT_GAIN_CONFIG,
    ...config,
  };

  // Beyond max distance = SILENT (0%) - HARD CUTOFF
  if (distance >= maxDistance) return 0;

  // Full volume within minimum distance
  if (distance <= minDistance) return 100;

  // CUBIC EXPONENTIAL FALLOFF for noticeable volume changes
  // Formula: gain = 100 * (1 - normalized)³
  // The cubed term makes volume drop STEEPER than squared
  //
  // Example: At 3m distance, gain ≈ 57% (clearly quieter!)
  // Example: At 5m distance, gain ≈ 33% (MUCH quieter!)
  // Example: At 10m distance, gain ≈ 4% (barely audible)
  // Example: At 15m+ distance, gain = 0% (SILENT)

  const range = maxDistance - minDistance;
  const normalizedDistance = (distance - minDistance) / range;

  // CUBIC falloff: (1 - x)³ where x is 0 to 1
  const remainingRatio = 1 - normalizedDistance;
  const gain = 100 * remainingRatio * remainingRatio * remainingRatio;

  // Ensure we never go negative
  return Math.max(0, gain);
}
